import { Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { PricePoint } from "./useDayAheadPrices";
import { formatDateTime } from "../../../util/formatDate";

const { Text } = Typography;

const SAMPLES_PER_DAY = 96;

type PriceRow = {
  key: number;
  timestamp: Date;
  actual: number | null;
  forecast: number | null;
};

type PriceTableProps = {
  actual: PricePoint[];
  forecast: PricePoint[];
  isLoading?: boolean;
};

const toRows = (actual: PricePoint[], forecast: PricePoint[]): PriceRow[] => {
  const rowByTime = new Map<number, PriceRow>();
  const rowAt = (timestamp: Date) => {
    const key = timestamp.getTime();
    const existing = rowByTime.get(key);
    if (existing) {
      return existing;
    }
    const created: PriceRow = { key, timestamp, actual: null, forecast: null };
    rowByTime.set(key, created);
    return created;
  };

  for (const point of actual) {
    rowAt(point.timestamp).actual = point.value;
  }
  for (const point of forecast) {
    rowAt(point.timestamp).forecast = point.value;
  }

  return [...rowByTime.values()].sort(
    (left, right) => left.timestamp.getTime() - right.timestamp.getTime(),
  );
};

const difference = (row: PriceRow) =>
  row.actual === null || row.forecast === null
    ? null
    : row.forecast - row.actual;

const renderPrice = (value: number | null) =>
  value === null ? <Text type="secondary">—</Text> : value.toFixed(2);

// Sorting a column that is missing on some rows should group the blanks
// together instead of interleaving them with real prices.
const byOptionalValue = (left: number | null, right: number | null) => {
  if (left === null) {
    return right === null ? 0 : 1;
  }
  if (right === null) {
    return -1;
  }
  return left - right;
};

export const PriceTable: React.FC<PriceTableProps> = ({
  actual,
  forecast,
  isLoading,
}) => {
  const rows = toRows(actual, forecast);

  const columns: ColumnsType<PriceRow> = [
    {
      title: "Time",
      dataIndex: "timestamp",
      key: "timestamp",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
      render: (timestamp: Date) => formatDateTime(timestamp),
    },
    {
      title: "Actual (€/MWh)",
      dataIndex: "actual",
      key: "actual",
      align: "right",
      sorter: (a, b) => byOptionalValue(a.actual, b.actual),
      render: renderPrice,
    },
    {
      title: "Forecast (€/MWh)",
      dataIndex: "forecast",
      key: "forecast",
      align: "right",
      sorter: (a, b) => byOptionalValue(a.forecast, b.forecast),
      render: renderPrice,
    },
    {
      title: "Difference (€/MWh)",
      key: "difference",
      align: "right",
      sorter: (a, b) => byOptionalValue(difference(a), difference(b)),
      render: (_: unknown, row: PriceRow) => {
        const delta = difference(row);
        if (delta === null) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Text
            type={delta === 0 ? undefined : delta > 0 ? "danger" : "success"}
          >
            {delta > 0 ? "+" : ""}
            {delta.toFixed(2)}
          </Text>
        );
      },
    },
  ];

  return (
    <Table<PriceRow>
      dataSource={rows}
      columns={columns}
      loading={isLoading}
      size="small"
      pagination={{
        defaultPageSize: SAMPLES_PER_DAY,
        showSizeChanger: true,
        pageSizeOptions: [
          SAMPLES_PER_DAY / 4,
          SAMPLES_PER_DAY / 2,
          SAMPLES_PER_DAY,
          SAMPLES_PER_DAY * 2,
        ],
      }}
    />
  );
};
